import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import apiClient from '../client';
import {
  GenderView,
  HobbyView,
  HobbyInput,
  ApiError,
  ApiPlatformCollectionResponse,
  extractCollectionItems,
} from '../types';

export const genderKeys = {
  all: ['genders'] as const,
  lists: () => [...genderKeys.all, 'list'] as const,
  list: (locale?: string) => [...genderKeys.lists(), { locale }] as const,
  details: () => [...genderKeys.all, 'detail'] as const,
  detail: (id: number, locale?: string) =>
    [...genderKeys.details(), id, { locale }] as const,
};

export const hobbyKeys = {
  all: ['hobbies'] as const,
  lists: () => [...hobbyKeys.all, 'list'] as const,
  list: (locale?: string) => [...hobbyKeys.lists(), { locale }] as const,
  details: () => [...hobbyKeys.all, 'detail'] as const,
  detail: (id: number, locale?: string) =>
    [...hobbyKeys.details(), id, { locale }] as const,
};


export const useGenders = (
  locale?: string,
  options?: Omit<UseQueryOptions<GenderView[], ApiError>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<GenderView[], ApiError>({
    queryKey: genderKeys.list(locale),
    queryFn: async () => {
      const params = locale ? { locale } : undefined;
      const response = await apiClient.get<ApiPlatformCollectionResponse<GenderView>>('/genders', params);
      return extractCollectionItems(response.data);
    },
    ...options,
  });
};

export const useGender = (
  id: number,
  locale?: string,
  options?: Omit<UseQueryOptions<GenderView, ApiError>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<GenderView, ApiError>({
    queryKey: genderKeys.detail(id, locale),
    queryFn: async () => {
      const params = locale ? { locale } : undefined;
      const response = await apiClient.get<GenderView>(
        `/genders/${id}`,
        params
      );
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};


export const useHobbies = (
  locale?: string,
  options?: Omit<UseQueryOptions<HobbyView[], ApiError>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<HobbyView[], ApiError>({
    queryKey: hobbyKeys.list(locale),
    queryFn: async () => {
      const params = locale ? { locale } : undefined;
      const response = await apiClient.get<ApiPlatformCollectionResponse<HobbyView>>('/hobbies', params);
      return extractCollectionItems(response.data);
    },
    ...options,
  });
};

export const useHobby = (
  id: number,
  locale?: string,
  options?: Omit<UseQueryOptions<HobbyView, ApiError>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<HobbyView, ApiError>({
    queryKey: hobbyKeys.detail(id, locale),
    queryFn: async () => {
      const params = locale ? { locale } : undefined;
      const response = await apiClient.get<HobbyView>(
        `/hobbies/${id}`,
        params
      );
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

export const useCreateHobby = (
  options?: UseMutationOptions<HobbyView, ApiError, HobbyInput>
) => {
  const queryClient = useQueryClient();

  return useMutation<HobbyView, ApiError, HobbyInput>({
    mutationFn: async (data: HobbyInput) => {
      const response = await apiClient.post<HobbyView>('/hobbies', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hobbyKeys.lists() });
    },
    ...options,
  });
};

export const useUpdateHobby = (
  options?: UseMutationOptions<
    HobbyView,
    ApiError,
    { id: number; data: HobbyInput }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    HobbyView,
    ApiError,
    { id: number; data: HobbyInput }
  >({
    mutationFn: async ({ id, data }: { id: number; data: HobbyInput }) => {
      const response = await apiClient.put<HobbyView>(
        `/hobbies/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: (data: HobbyView, variables: { id: number; data: HobbyInput }) => {
      queryClient.setQueryData(
        hobbyKeys.detail(variables.id),
        data
      );
      queryClient.invalidateQueries({ queryKey: hobbyKeys.lists() });
    },
    ...options,
  });
};

export const usePatchHobby = (
  options?: UseMutationOptions<
    HobbyView,
    ApiError,
    { id: number; data: Partial<HobbyInput> }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    HobbyView,
    ApiError,
    { id: number; data: Partial<HobbyInput> }
  >({
    mutationFn: async ({ id, data }: { id: number; data: Partial<HobbyInput> }) => {
      const response = await apiClient.patch<HobbyView>(
        `/hobbies/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: (data: HobbyView, variables: { id: number; data: Partial<HobbyInput> }) => {
      queryClient.setQueryData(
        hobbyKeys.detail(variables.id),
        data
      );
      queryClient.invalidateQueries({ queryKey: hobbyKeys.lists() });
    },
    ...options,
  });
};

